import { useCallback, useEffect, useReducer, useRef } from 'react';

import { ApiError } from '@/lib/errors';

import { assistantService } from '../services/assistantService';
import { ServerFrameSchema, type AssistantMessage, type AssistantStatus } from '../types/assistant';

interface State {
  status: AssistantStatus;
  messages: AssistantMessage[];
  error: string | null;
}

type Action =
  | { type: 'connecting' }
  | { type: 'ready'; history: AssistantMessage[] }
  | { type: 'asking'; user: AssistantMessage; assistant: AssistantMessage }
  | { type: 'delta'; clientMessageId: string; token: string }
  | { type: 'done'; clientMessageId: string; messageId: string }
  | { type: 'error'; message: string; clientMessageId?: string }
  | { type: 'unauthorized' }
  | { type: 'reconnecting' };

const INITIAL: State = { status: 'idle', messages: [], error: null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'connecting':
      return { ...state, status: 'connecting', error: null };
    case 'ready':
      return { status: 'ready', messages: action.history, error: null };
    case 'asking':
      return {
        ...state,
        status: 'asking',
        error: null,
        messages: [...state.messages, action.user, action.assistant],
      };
    case 'delta':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.clientMessageId === action.clientMessageId
            ? { ...m, content: m.content + action.token }
            : m,
        ),
      };
    case 'done':
      return {
        ...state,
        status: 'ready',
        messages: state.messages.map((m) =>
          m.clientMessageId === action.clientMessageId
            ? { ...m, id: action.messageId, streaming: false, clientMessageId: undefined }
            : m,
        ),
      };
    case 'error':
      return {
        ...state,
        status: 'ready',
        error: action.message,
        messages: state.messages.map((m) =>
          m.clientMessageId === action.clientMessageId
            ? { ...m, incomplete: true, streaming: false, clientMessageId: undefined }
            : m,
        ),
      };
    case 'unauthorized':
      return { status: 'unauthorized', messages: [], error: null };
    case 'reconnecting':
      return { ...state, status: 'reconnecting' };
    default:
      return state;
  }
}

// Derive the realtime WebSocket origin from the same HTTP base used by the
// REST realtime client (VITE_REALTIME_BASE_URL — e.g.
// `https://realtime-dev-myndwell.elixaa.ai/api/v1`). The `/api/v1` suffix is
// REST-only; the WS endpoint sits at the host root, and the ticket's `wsPath`
// (e.g. `/ws/assistant`) is appended at connection time.
function deriveWsBase(): string {
  const httpBase =
    (import.meta.env.VITE_REALTIME_BASE_URL as string | undefined) ??
    'http://localhost:8801/api/v1';
  try {
    const u = new URL(httpBase);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.pathname = '';
    u.search = '';
    return u.toString().replace(/\/$/, '');
  } catch {
    return 'ws://localhost:8801';
  }
}

const REALTIME_WS_BASE: string = deriveWsBase();

export interface UseAssistantSessionOptions {
  enabled: boolean;
}

export interface AssistantSessionApi {
  status: AssistantStatus;
  messages: AssistantMessage[];
  error: string | null;
  ensureConnected: () => void;
  ask: (text: string) => void;
}

export function useAssistantSession(
  sessionId: string,
  options: UseAssistantSessionOptions,
): AssistantSessionApi {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const wsRef = useRef<WebSocket | null>(null);
  const connectedOnceRef = useRef(false);
  const enabledRef = useRef(options.enabled);
  const attemptRef = useRef(0);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const disposedRef = useRef(false);
  useEffect(() => {
    enabledRef.current = options.enabled;
  }, [options.enabled]);

  const connect = useCallback(async () => {
    if (disposedRef.current || !enabledRef.current) return;
    if (wsRef.current) return;
    dispatch({ type: 'connecting' });
    try {
      const ticket = await assistantService.mintTicket(sessionId);
      // The panel may have unmounted while the ticket was in flight. TS keeps
      // the narrowing from the guard above across the await, hence the disable.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (disposedRef.current) return;
      const url = `${REALTIME_WS_BASE}${ticket.wsPath}?ticket=${encodeURIComponent(ticket.ticket)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30_000);
      };

      ws.onmessage = (e: MessageEvent) => {
        const raw = e.data as string;
        let parsedJson: unknown;
        try {
          parsedJson = JSON.parse(raw);
        } catch {
          return;
        }
        const parsed = ServerFrameSchema.safeParse(parsedJson);
        if (!parsed.success) return;
        const frame = parsed.data;
        if (frame.type === 'ready') {
          dispatch({
            type: 'ready',
            history: frame.history.map((m) => ({
              id: m.id,
              role: m.role,
              content: m.content,
              incomplete: m.incomplete,
              createdAt: m.createdAt,
            })),
          });
        } else if (frame.type === 'delta') {
          dispatch({ type: 'delta', clientMessageId: frame.clientMessageId, token: frame.token });
        } else if (frame.type === 'done') {
          dispatch({
            type: 'done',
            clientMessageId: frame.clientMessageId,
            messageId: frame.messageId,
          });
        } else if (frame.type === 'error') {
          dispatch({
            type: 'error',
            message: frame.message,
            clientMessageId: frame.clientMessageId,
          });
        } else if (frame.type === 'cancelled') {
          dispatch({
            type: 'error',
            message: '',
            clientMessageId: frame.clientMessageId,
          });
        } else if (frame.type === 'unauthorized') {
          dispatch({ type: 'unauthorized' });
          ws.close();
        }
      };

      ws.onclose = (e: CloseEvent) => {
        // A socket already replaced or dropped by cleanup must not touch the
        // live one's ref or heartbeat.
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        // Don't reconnect after unmount, clean close (1000) or unauthorized.
        // 4003 is the server's unauthorized close — also terminal.
        if (disposedRef.current || e.code === 1000 || e.code === 4003) return;
        // Backoff 1s, 2s, 4s, 8s, 16s, capped at 30s.
        const attempt = attemptRef.current;
        attemptRef.current = attempt + 1;
        const delay = Math.min(30_000, 1000 * 2 ** attempt);
        dispatch({ type: 'reconnecting' });
        reconnectTimerRef.current = setTimeout(() => {
          void connectRef.current();
        }, delay);
      };
    } catch (err) {
      wsRef.current = null;
      // Same stale narrowing as after the ticket await.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (disposedRef.current) return;
      // A 4xx refusal (session ended, not the assigned practitioner) will not
      // change on retry. Timeouts and rate limits can. A 401 is the api
      // client's to handle (refresh or redirect to login), and a retry that
      // fails after a good refresh still surfaces as the original 401.
      if (
        err instanceof ApiError &&
        err.status >= 400 &&
        err.status < 500 &&
        err.status !== 401 &&
        err.status !== 408 &&
        err.status !== 429
      ) {
        dispatch({ type: 'unauthorized' });
        return;
      }
      // Network or server failure — schedule a reconnect attempt.
      const attempt = attemptRef.current;
      attemptRef.current = attempt + 1;
      const delay = Math.min(30_000, 1000 * 2 ** attempt);
      dispatch({ type: 'reconnecting' });
      reconnectTimerRef.current = setTimeout(() => {
        void connectRef.current();
      }, delay);
    }
  }, [sessionId]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const ensureConnected = useCallback(() => {
    if (connectedOnceRef.current) return;
    connectedOnceRef.current = true;
    void connect();
  }, [connect]);

  const ask = useCallback((text: string) => {
    const ws = wsRef.current;
    if (ws?.readyState !== 1) return;
    const clientMessageId = crypto.randomUUID();
    const now = new Date().toISOString();
    const user: AssistantMessage = {
      id: `local-${clientMessageId}-u`,
      role: 'user',
      content: text,
      incomplete: false,
      createdAt: now,
    };
    const assistant: AssistantMessage = {
      id: `local-${clientMessageId}-a`,
      role: 'assistant',
      content: '',
      incomplete: false,
      createdAt: now,
      streaming: true,
      clientMessageId,
    };
    dispatch({ type: 'asking', user, assistant });
    ws.send(JSON.stringify({ type: 'ask', message: text, clientMessageId }));
  }, []);

  useEffect(() => {
    // Reset on mount: StrictMode runs this cleanup once before the real mount.
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      // If React re-runs this effect on a live instance (Fast Refresh), the
      // panel's ensureConnected must be able to open a fresh socket.
      connectedOnceRef.current = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close(1000);
      wsRef.current = null;
    };
  }, []);

  return {
    status: state.status,
    messages: state.messages,
    error: state.error,
    ensureConnected,
    ask,
  };
}
